import { strftimeToMomentFormat } from '@calendly/booking-kit/services/time_format'
import { I18n } from '@calendly/i18n'
import moment from 'moment'


export const parseTime = (text, notation = '24h') => (
  moment(text, notation === '24h' ? 'HH:mm' : 'hh:mma', true)
)

export const formatTime = (timeMoment, notation = '12h', format12hr) => {
  if (notation === '12h') {
    return timeMoment.format(format12hr || 'h:mma')
  }
  return timeMoment.format('HH:mm')
}

export const formatDate = (dateMoment, strftimeFormat, locale = I18n.locale) => {
  const momentFormat = strftimeToMomentFormat(strftimeFormat)
  return dateMoment.clone().locale(locale).format(momentFormat)
}

/**
 * Map API date notation values to locale format keys.
 * Locale files use "american" and "european_standard"; API may return "mdy", "MM/DD/YYYY", etc.
 */
const normalizeDateNotation = (userDateNotation) => {
  const n = (userDateNotation || 'american').toLowerCase()
  if (n === 'mdy' || n === 'mm/dd/yyyy') return 'american'
  if (n === 'dmy' || n === 'dd/mm/yyyy') return 'european_standard'
  if (n === 'american' || n === 'european_standard') return n
  return 'american'
}

export const userDateFormat = (
  dateMoment,
  userDateNotation = 'american',
  format = 'date_large',
  locale = I18n.locale
) => {
  const notation = normalizeDateNotation(userDateNotation)
  format = I18n.t(`time.formats.${notation}.${format}`, { locale })
  return formatDate(dateMoment, format, locale)
}

export const userDateFormatCheckYear = (
  dateMoment,
  userDateNotation = 'american',
  withoutYearformat = 'date_extra',
  withYearFormat = 'date_full',
  locale = I18n.locale
) => {
  const format = dateMoment.year() === moment().year() ? withoutYearformat : withYearFormat
  return userDateFormat(dateMoment, userDateNotation, format, locale)
}

export const userDateTimeFormat = (
  dateMoment,
  userDateNotation = 'american',
  userTimeNotation = '12h',
  format = 'date_time_full',
  locale = I18n.locale
) => {
  const notation = normalizeDateNotation(userDateNotation)
  format = I18n.t(`time.formats.${notation}.${userTimeNotation}.${format}`, { locale })
  return formatDate(dateMoment, format, locale)
}

export const userDateRangeFormat = (
  fromMoment,
  toMoment,
  dateNotation,
  withoutYearFormat,
  withYearFormat
) => {
  const fromFormat = fromMoment.year() === toMoment.year() ? withoutYearFormat : withYearFormat

  const fromStr = userDateFormat(fromMoment, dateNotation, fromFormat)
  const toStr = userDateFormat(toMoment, dateNotation, withYearFormat)

  return [fromStr, toStr]
}

export const shortUserDateRangeFormat = (
  fromMoment,
  toMoment,
  dateNotation = 'american',
  withoutYearFormat = 'date_small',
  withYearFormat = 'date_small_with_year'
) => {
  const fromFormat = fromMoment.year() === toMoment.year() ? withoutYearFormat : withYearFormat

  const fromStr = userDateFormat(fromMoment, dateNotation, fromFormat)
  let toStr = userDateFormat(toMoment, dateNotation, 'date_small_with_year')

  if (fromMoment.isSame(toMoment, 'month')) {
    toStr = toStr.split(' ').filter((datePart, i) => i !== 0).join(' ')
  }

  return [fromStr, toStr]
}

export const shortUserDateRangeFormatWithNoYear = (
  fromMoment,
  toMoment,
  dateNotation = 'american',
) => {
  const fromStr = userDateFormat(fromMoment, dateNotation, 'date_small')
  let toStr = userDateFormat(toMoment, dateNotation, 'date_small')

  if (fromMoment.isSame(toMoment, 'month')) {
    toStr = toStr.split(' ').filter((datePart, i) => i !== 0).join(' ')
  }

  return [fromStr, toStr]
}

export const formatTimeRange = (
  startTimeMoment,
  endTimeMoment,
  userTimeNotation = '12h',
  includeTimezone = false
) => {
  if (userTimeNotation !== '12h') {
    return `${startTimeMoment.format('HH:mm')} – ${endTimeMoment.format('HH:mm')}`
  }

  let start = startTimeMoment.format('h:mm a')
  let end = endTimeMoment.format('h:mm a')

  // Remove :00 when top of the hour
  start = start.replace(':00', '')
  end = end.replace(':00', '')
  let range = `${start} – ${end}`

  if (includeTimezone) {
    range += ` (${startTimeMoment.format('z')})`
  }

  return range
}
